/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { type FeedbackReason } from 'uplisting-frontend/models/ai-draft';

export interface FeedbackResult {
  positiveFeedback: boolean;
  feedbackReason?: FeedbackReason;
  feedbackCustomReason?: string;
}

interface IArgs {
  showThumbsUpDown: boolean;
  requireReason: boolean;
  onSubmit(result: FeedbackResult): void;
  onSkip(): void;
  onCancel(): void;
}

interface AIDraftFeedbackSignature {
  Element: HTMLDivElement;
  Args: IArgs;
}

enum FeedbackReasonsEnum {
  incorrectInformation = 'incorrect_information',
  wrongTone = 'wrong_tone',
  tooLong = 'too_long',
  missingDetails = 'missing_details',
  irrelevantContent = 'irrelevant_content',
  inappropriate = 'inappropriate',
  other = 'other',
}

export default class UiAIDraftFeedbackComponent extends Component<AIDraftFeedbackSignature> {
  @tracked positiveFeedback?: boolean;
  @tracked feedbackReason?: FeedbackReason;
  @tracked feedbackCustomReason = '';
  @tracked showReasonSelection = false;

  get showOtherInput(): boolean {
    return this.feedbackReason === 'other';
  }

  get showThumbsUpDown(): boolean {
    return this.args.showThumbsUpDown;
  }

  get requireReason(): boolean {
    return this.args.requireReason;
  }

  get canSubmit(): boolean {
    if (this.feedbackReason === 'other') {
      return this.feedbackCustomReason.trim().length > 0;
    }

    if (this.requireReason) {
      return this.feedbackReason !== undefined;
    }

    if (this.showThumbsUpDown) {
      return this.positiveFeedback !== undefined;
    }

    return true;
  }

  get feedbackReasons(): FeedbackReason[] {
    return Object.values(FeedbackReasonsEnum);
  }

  @action
  handleThumbsUp(): void {
    this.positiveFeedback = true;
    this.feedbackReason = undefined;
    this.showReasonSelection = false;
  }

  @action
  handleThumbsDown(): void {
    this.positiveFeedback = false;
    this.showReasonSelection = true;
  }

  @action
  handleSelectReason(reason: FeedbackReason): void {
    this.feedbackReason = reason;
    if (reason !== 'other') {
      this.feedbackCustomReason = '';
    }
  }

  @action
  handleCustomReasonInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.feedbackCustomReason = target.value;
  }

  @action
  handleSubmit(): void {
    this.args.onSubmit({
      positiveFeedback: this.positiveFeedback ?? false,
      feedbackReason: this.feedbackReason,
      feedbackCustomReason:
        this.feedbackReason === 'other'
          ? this.feedbackCustomReason.trim()
          : undefined,
    });
  }

  @action
  handleSkip(): void {
    this.args.onSkip();
  }

  @action
  handleCancel(): void {
    this.args.onCancel();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::AiDraftFeedback': typeof UiAIDraftFeedbackComponent;
  }
}
